import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError, formatCurrency, formatDate } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Eye, UserCog } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function AdminTableView() {
  const { tableId } = useParams();
  const { user } = useAuth();
  const expired = user?.subscription_active === false;

  const [order, setOrder] = useState(null);
  const [table, setTable] = useState(null);
  const [waiters, setWaiters] = useState([]);
  const [selectedWaiter, setSelectedWaiter] = useState("");
  const [reassigning, setReassigning] = useState(false);

  const load = async () => {
    try {
      const [tablesRes, waitersRes] = await Promise.all([
        api.get("/tables"),
        api.get("/admin/waiters"),
      ]);
      const t = tablesRes.data.find(x => x.id === tableId);
      setTable(t);
      setWaiters(waitersRes.data.filter(w => w.active !== false));
      if (t?.current_order_id) {
        const { data } = await api.get(`/orders/${t.current_order_id}`);
        setOrder(data);
        setSelectedWaiter(data.waiter_id);
      } else {
        setOrder(null);
      }
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tableId]);

  const reassign = async () => {
    if (!order || !selectedWaiter || selectedWaiter === order.waiter_id) return;
    setReassigning(true);
    try {
      await api.put(`/admin/orders/${order.id}/waiter`, { waiter_id: selectedWaiter });
      toast.success("Mesero reasignado");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setReassigning(false); }
  };

  if (!table) return <p className="text-[#7248ae]">Cargando…</p>;

  return (
    <div className="animate-fade-up max-w-3xl" data-testid="admin-table-view">
      <Link to="/admin/tables" className="inline-flex items-center text-sm text-[#7248ae] hover:text-[#3e166c] mb-6">
        <ArrowLeft className="h-4 w-4 mr-2" />Mesas
      </Link>

      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-[#7248ae] mb-2">Mesa</p>
          <h1 className="font-display text-5xl font-black text-[#1a0f2e]">{table.number}</h1>
          <p className="text-[#7248ae]">{table.capacity} pers. · {table.location || "Sin ubicación"}</p>
        </div>
        <Badge className={table.status === "occupied" ? "bg-[#3e166c]" : "bg-[#10b981]"}>
          {table.status === "occupied" ? "Ocupada" : "Libre"}
        </Badge>
      </div>

      <div className="flex items-center gap-2 mb-6 p-3 rounded-xl bg-[#3e166c]/5 border border-[#b182ec]/30">
        <Eye className="h-4 w-4 text-[#7248ae]" />
        <p className="text-sm text-[#7248ae]">Vista de admin: solo lectura. Solo puedes reasignar el mesero a esta mesa.</p>
      </div>

      {!order ? (
        <Card className="p-8 text-center border-dashed border-[#b182ec]/40">
          <p className="text-[#7248ae]">Esta mesa está libre.</p>
        </Card>
      ) : (
        <>
          <Card className="p-6 border border-[#b182ec]/30 rounded-2xl mb-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs uppercase tracking-wider text-[#7248ae]">Atendida por</p>
                <p className="font-display text-xl font-bold text-[#1a0f2e]">{order.waiter_name}</p>
                <p className="text-sm text-[#7248ae]">Abierta {formatDate(order.created_at)}</p>
              </div>
            </div>
          </Card>

          <Card className="p-5 border border-[#b182ec]/30 rounded-2xl mb-4" data-testid="reassign-card">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-[#3e166c]/10 flex items-center justify-center">
                <UserCog className="h-5 w-5 text-[#3e166c]" />
              </div>
              <div>
                <h3 className="font-display text-lg font-bold text-[#1a0f2e]">Reasignar mesero</h3>
                <p className="text-sm text-[#7248ae]">Útil en cambios de turno.</p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Select value={selectedWaiter} onValueChange={setSelectedWaiter} disabled={expired}>
                <SelectTrigger className="flex-1 border-[#b182ec]/50" data-testid="reassign-select">
                  <SelectValue placeholder="Elige un mesero" />
                </SelectTrigger>
                <SelectContent>
                  {waiters.map(w => (
                    <SelectItem key={w.id} value={w.id} data-testid={`reassign-option-${w.email}`}>
                      {w.name} ({w.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                onClick={reassign}
                disabled={expired || reassigning || !selectedWaiter || selectedWaiter === order.waiter_id}
                className="bg-[#3e166c] hover:bg-[#7248ae] text-white"
                data-testid="reassign-submit-button"
              >
                {reassigning ? "Asignando…" : "Reasignar"}
              </Button>
            </div>
          </Card>

          <Card className="p-6 border border-[#b182ec]/30 rounded-2xl">
            <h3 className="font-display text-lg font-bold text-[#1a0f2e] mb-3">Platillos</h3>
            <ul className="space-y-2 mb-5" data-testid="order-items">
              {order.items.map(it => {
                const modsDelta = (it.modifiers || []).reduce((a, m) => a + Number(m.price_delta || 0), 0);
                const unit = Number(it.price) + modsDelta;
                return (
                  <li key={it.id} className="p-3 rounded-lg bg-[#fcfbfd] border border-[#b182ec]/20">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <p className="text-[#1a0f2e]"><strong>{it.quantity}×</strong> {it.name}</p>
                        {(it.modifiers || []).length > 0 && (
                          <p className="text-xs text-[#7248ae] mt-0.5">+ {(it.modifiers || []).map(m => m.name).join(", ")}</p>
                        )}
                        {it.notes && <p className="text-xs text-[#7248ae] italic mt-1">📝 {it.notes}</p>}
                      </div>
                      <span className="text-[#3e166c] font-semibold">{formatCurrency(unit * it.quantity)}</span>
                    </div>
                  </li>
                );
              })}
              {order.items.length === 0 && <p className="text-[#7248ae] text-sm">Aún sin platillos.</p>}
            </ul>
            <div className="border-t border-[#b182ec]/30 pt-4 flex items-center justify-between">
              <span className="text-xs uppercase tracking-wider text-[#7248ae]">Total</span>
              <span className="font-display text-3xl font-black text-[#3e166c]">{formatCurrency(order.total)}</span>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
